/**
 * ALBCAP-a · capacidad de albergue con la regla del propio plan.
 *
 * La plantilla del PTEL (Anexo VI, fuente DGPC 1989) dice: aforo = área / 10, «siempre que se
 * cumpla que haya un lavabo y un inodoro por cada 50 personas, y una ducha por cada 100».
 *
 * DOP selladas por Luis (30-sep-2026, «ok»):
 * - DOP-1 a′: regla literal. Plazas = mín(⌊área cerrada ÷ 10⌋, 50 × mín(inodoros, lavabos),
 *   100 × duchas). Sin duchas salen 0 plazas, pero siempre se da la cifra por superficie y el
 *   límite que manda, para que el 0 no quede opaco.
 * - DOP-2 i: sin área cerrada → «no calculable»; si falta un dato de aseos o duchas se calcula
 *   con lo que haya y se avisa de que esa condición no se pudo comprobar.
 * - DOP-3: dos avisos de cotejo con el aforo del plan: supera lo que da la superficie cerrada
 *   (tolerancia ±1 plaza) o parece salir del área abierta. Por debajo no avisa.
 * - DOP-6: sin Esfera. DOP-7: también se calcula si la tipología no lo da como apto, con aviso.
 * Solo cuenta el área cerrada: el área abierta es dato del recinto, nunca plazas.
 */
import type { EstadoAlbergue } from './aptitudAlbergue';
import { cifraRAE, type FichaAlbergue } from './fichaAlbergue';

/** Qué manda en la cifra final. */
export type LimiteCapacidad = 'superficie' | 'aseos' | 'duchas';

export interface CapacidadAlbergue {
  /** `false` si el plan no da el área cerrada. */
  calculable: boolean;
  /** Plazas según la regla del plan (con la condición de aseos y duchas). */
  plazas?: number;
  /** ⌊área cerrada ÷ 10⌋, sin la condición. */
  porSuperficie?: number;
  /** Límite que manda en `plazas`. */
  limite?: LimiteCapacidad;
  /** 50 × mín(inodoros, lavabos) con los datos que haya. */
  topeAseos?: number;
  /** 100 × duchas. */
  topeDuchas?: number;
  /** Avisos en minúscula y sin punto final. */
  avisos: string[];
}

const M2_POR_PERSONA = 10;
const PERSONAS_POR_ASEO = 50;
const PERSONAS_POR_DUCHA = 100;
/** Diferencia, en plazas, que se tolera al cotejar el aforo del plan con la regla (redondeos). */
const TOLERANCIA = 1;

export const AVISOS_CAPACIDAD = {
  sinArea: 'el plan no da la superficie cerrada',
  sinAseos: 'condición de aseos sin comprobar: el plan no da inodoros ni lavabos',
  sinInodoros: 'condición de aseos incompleta: el plan no da inodoros',
  sinLavabos: 'condición de aseos incompleta: el plan no da lavabos',
  sinDuchas: 'condición de duchas sin comprobar: el plan no da duchas',
  supera: (dec: number, sup: number) =>
    `el aforo del plan (${cifraRAE(dec)}) supera lo que da la superficie cerrada (${cifraRAE(sup)})`,
  abierta: (dec: number, m2: number) =>
    `el aforo del plan (${cifraRAE(dec)}) parece salir del área abierta (${cifraRAE(m2)} m² ÷ 10), que no cuenta como plazas`,
  noApto: 'el plan lo pone en su tabla de albergue, pero por su tipología no se da como apto',
} as const;

const casa = (dec: number, m2?: number) =>
  m2 !== undefined && m2 > 0 && Math.abs(dec - Math.floor(m2 / M2_POR_PERSONA)) <= TOLERANCIA;

/**
 * Capacidad de albergue de un elemento con ficha del plan. Sin ficha devuelve `undefined`.
 *
 * @param ficha          - Ficha del Anexo VI (`ALBFICHA`).
 * @param aforoDeclarado - Aforo que escribe el autor del plan, si lo hay.
 * @param estado         - Aptitud de `ALBTIPO`, para el aviso de la DOP-7.
 */
export function capacidadAlbergue(
  ficha: FichaAlbergue | undefined,
  aforoDeclarado?: number | null,
  estado?: EstadoAlbergue,
): CapacidadAlbergue | undefined {
  if (!ficha) return undefined;
  const avisos: string[] = [];
  const cerrada = ficha.areaCerradaM2;
  const noApto = estado !== undefined && estado !== 'apto';

  if (cerrada === undefined) {
    // El motivo ya lo dice la línea («No calculable: …»): no se repite como aviso.
    if (noApto) avisos.push(AVISOS_CAPACIDAD.noApto);
    return { calculable: false, avisos };
  }

  const porSuperficie = Math.floor(Math.max(0, cerrada) / M2_POR_PERSONA);
  const aseos = [ficha.inodoros, ficha.lavabos].filter((n): n is number => n !== undefined);
  const topeAseos = aseos.length ? PERSONAS_POR_ASEO * Math.min(...aseos) : undefined;
  const topeDuchas = ficha.duchas !== undefined ? PERSONAS_POR_DUCHA * ficha.duchas : undefined;

  let plazas = porSuperficie;
  let limite: LimiteCapacidad = 'superficie';
  if (topeAseos !== undefined && topeAseos < plazas) { plazas = topeAseos; limite = 'aseos'; }
  if (topeDuchas !== undefined && topeDuchas < plazas) { plazas = topeDuchas; limite = 'duchas'; }

  if (ficha.inodoros === undefined && ficha.lavabos === undefined) avisos.push(AVISOS_CAPACIDAD.sinAseos);
  else if (ficha.inodoros === undefined) avisos.push(AVISOS_CAPACIDAD.sinInodoros);
  else if (ficha.lavabos === undefined) avisos.push(AVISOS_CAPACIDAD.sinLavabos);
  if (ficha.duchas === undefined) avisos.push(AVISOS_CAPACIDAD.sinDuchas);

  if (aforoDeclarado != null && aforoDeclarado > 0 && !casa(aforoDeclarado, cerrada)) {
    if (casa(aforoDeclarado, ficha.areaAbiertaM2)) {
      avisos.push(AVISOS_CAPACIDAD.abierta(aforoDeclarado, ficha.areaAbiertaM2 as number));
    } else if (aforoDeclarado > porSuperficie + TOLERANCIA) {
      avisos.push(AVISOS_CAPACIDAD.supera(aforoDeclarado, porSuperficie));
    }
  }
  if (noApto) avisos.push(AVISOS_CAPACIDAD.noApto);

  return { calculable: true, plazas, porSuperficie, limite, topeAseos, topeDuchas, avisos };
}

const plazasTxt = (n: number) => `${cifraRAE(n)} ${n === 1 ? 'plaza' : 'plazas'}`;

/**
 * Línea legible para P2 (y para la exportación en `ALBCAP-b`). Nunca un 0 sin su motivo.
 * Ej.: «0 plazas: no tiene duchas (por superficie serían 117)».
 */
export function lineaCapacidadAlbergue(c: CapacidadAlbergue): string {
  if (!c.calculable || c.plazas === undefined || c.porSuperficie === undefined) {
    return `No calculable: ${AVISOS_CAPACIDAD.sinArea}`;
  }
  const sup = `por superficie serían ${cifraRAE(c.porSuperficie)}`;
  if (c.limite === 'duchas') {
    return c.plazas === 0
      ? `0 plazas: no tiene duchas (${sup})`
      : `${plazasTxt(c.plazas)}: las duchas no dan para más (${sup})`;
  }
  if (c.limite === 'aseos') {
    return c.plazas === 0
      ? `0 plazas: no tiene inodoros o lavabos (${sup})`
      : `${plazasTxt(c.plazas)}: los aseos no dan para más (${sup})`;
  }
  return c.plazas === 0
    ? '0 plazas: no tiene superficie cerrada'
    : `${plazasTxt(c.plazas)} (superficie cerrada ÷ 10 m² por persona)`;
}
